import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import styles from "../LineChart/LineChart.module.css";

Chart.register(...registerables);

const SERIES_SLOTS = 5;

const LineAndBarChart = ({
  title = "Chart",
  icon,
  labels = [],
  datasets = [],
  loading = false,
  error = null,
  emptyMessage = "No data available",
  height = 320,
  tension = 0.4,
  yMin,
  yMax,
  yStepSize,
  // Secondary (right) axis — used when a dataset declares `yAxisID: "y1"`.
  y1Min,
  y1Max,
  y1StepSize,
  y1AxisLabel = null,
  stackedBars = false,
  xAxisLabel = null,
  yAxisLabel = null,
  /* Show category labels under the x-axis (off by default for trend charts). */
  showXTicks = false,
  note = null,
  selectControl = null,
  // Toggle
  modes = null,
  activeMode = null,
  onModeChange,
  // Tooltip + optional table drilldown
  tooltipFormatter = null,
  /* Optional custom tooltip title. Receives the hovered dataIndex and may
   * return a single string OR an array of strings (multiple title lines —
   * title lines never render a colour swatch). Falls back to labels[idx]. */
  tooltipTitleFormatter = null,
  pointDetails = null,
  onPointClick,
  drilldownLoading = false,
  drilldownError = null,
  drilldownColumns = null,
  drilldownTitleSuffix = "",
  drilldownPerPage = 10,
  drilldownText = {},
  // Bar-level drilldown (parent-managed levels)
  barDrilldown = false,
  onBarClick,
  canGoBack = false,
  onBack,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [page, setPage] = useState(1);

  const hasData =
    Array.isArray(labels) &&
    labels.length > 0 &&
    Array.isArray(datasets) &&
    datasets.length > 0;

  const tableDrilldownEnabled =
    Array.isArray(pointDetails) && pointDetails.length > 0;
  const inTable = tableDrilldownEnabled && selectedIndex !== null;
  // Any click handling at all (table OR bar-level)
  const clickEnabled = tableDrilldownEnabled || barDrilldown;

  const dt = {
    showing: "Showing",
    to: "to",
    of: "of",
    entries: "entries",
    previous: "Previous",
    next: "Next",
    note: "Note:",
    ...drilldownText,
  };

  /* ── Shared destroy helper ─────────────────────────────────────────────────
   * Always nulls chartRef BEFORE calling destroy() so that any Chart.js
   * tooltip / hover callback that fires synchronously during teardown hits
   * the null-guard in onHover / onClick and bails out — preventing the
   * "getParsed" crash.  Never deferred: synchronous destroy is the only way
   * to guarantee the canvas is free before new Chart() runs next render.
   * ───────────────────────────────────────────────────────────────────────── */
  const destroyChart = () => {
    const instance = chartRef.current;
    chartRef.current = null; // null FIRST — guards onHover / onClick
    if (instance) {
      try {
        instance.destroy();
      } catch (_) {
        // swallow any internal Chart.js error during forced teardown
      }
    }
  };
const inBarValueLabels = {
  id: "inBarValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    chart.data.datasets.forEach((dataset, dsIndex) => {
      if (dataset.type && dataset.type !== "bar") return;
      const meta = chart.getDatasetMeta(dsIndex);
      if (!meta || meta.hidden || !meta.data) return;

      meta.data.forEach((bar, i) => {
        const value = dataset.data[i];
        if (value == null || Number(value) === 0) return;

        // getProps gives stable y/base even mid-animation
        const { x, y, base } = bar.getProps(["x", "y", "base"], true);
        if (x == null || y == null || base == null) return;

        const segHeight = Math.abs(base - y);
        if (segHeight < 10) return;

        ctx.save();
        ctx.fillStyle = "#1f2937";
        ctx.font = "bold 13px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(value), x, (y + base) / 2);
        ctx.restore();
      });
    });
  },
};
  useEffect(() => {
    if (!canvasRef.current || !hasData || loading || error || inTable) {
      destroyChart();
      return;
    }

    const ctx = canvasRef.current.getContext("2d");
    const cardStyle = getComputedStyle(canvasRef.current);
    const getToken = (name) =>
      name ? cardStyle.getPropertyValue(name).trim() : "";

    // Does any dataset want to live on the right-hand (secondary) axis?
    const usesSecondaryAxis = datasets.some((d) => d.yAxisID === "y1");

    const chartDatasets = datasets.map((ds, index) => {
      const slot = (index % SERIES_SLOTS) + 1;
      // Colour resolution order: explicit color → CSS var name → series slot.
      const color =
        ds.color ||
        (ds.colorVar ? getToken(ds.colorVar) : "") ||
        getToken(`--line-series-${slot}-color`) ||
        "#38bdf8";
      const isBar = ds.type === "bar";

      if (isBar) {
        // Solid fill by default; vertical gradient when CSS vars are supplied.
        let background = ds.barFillColor || color;
        if (ds.gradientDarkVar || ds.gradientLightVar) {
          const darkC =
            (ds.gradientDarkVar && getToken(ds.gradientDarkVar)) || color;
          const lightC =
            (ds.gradientLightVar && getToken(ds.gradientLightVar)) || darkC;
          background = (context) => {
            const chart = context.chart;
            const area = chart.chartArea;
            if (!area) return lightC; // layout not ready yet
            const grad = chart.ctx.createLinearGradient(
              0,
              area.top,
              0,
              area.bottom,
            );
            grad.addColorStop(0, darkC);
            grad.addColorStop(1, lightC);
            return grad;
          };
        }

        return {
          type: "bar",
          label: ds.label,
          data: ds.data,
          backgroundColor: background,
          borderColor: color,
          borderWidth: ds.borderWidth !== undefined ? ds.borderWidth : 0,
          borderRadius: ds.borderRadius !== undefined ? ds.borderRadius : 4,
          barPercentage:
            ds.barPercentage !== undefined ? ds.barPercentage : 0.7,
          categoryPercentage:
            ds.categoryPercentage !== undefined ? ds.categoryPercentage : 0.8,
          // Bars with the same `stack` value stack together.
          // When stackedBars is off, each bar gets a unique stack so they
          // render side-by-side instead of accidentally stacking.
          stack: ds.stack || (stackedBars ? "bars" : `bar-${index}`),
          order: ds.order !== undefined ? ds.order : 2, // bars below lines
          yAxisID: ds.yAxisID || "y",
        };
      }

      // Line dataset
      return {
        type: "line",
        label: ds.label,
        data: ds.data,
        borderColor: color,
        backgroundColor: "transparent",
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        pointRadius: ds.pointRadius !== undefined ? ds.pointRadius : 3,
        pointHoverRadius:
          ds.pointHoverRadius !== undefined ? ds.pointHoverRadius : 6,
        borderWidth: ds.borderWidth !== undefined ? ds.borderWidth : 2.5,
        borderDash: ds.borderDash || [],
        tension: ds.tension !== undefined ? ds.tension : tension,
        fill: false,
        // Unique stack so the line never gets folded into the bar stack
        stack: ds.stack || `line-${index}`,
        order: ds.order !== undefined ? ds.order : 1, // lines on top
        yAxisID: ds.yAxisID || "y",
      };
    });

    // Destroy any previous instance synchronously before creating the new one.
    // This is the ONLY reliable way to prevent "Canvas is already in use".
    destroyChart();

    chartRef.current = new Chart(ctx, {
      type: "bar", // base; per-dataset `type` overrides this
      data: { labels, datasets: chartDatasets },
      plugins: [inBarValueLabels],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        layout: { padding: { right: 12, top: 8, bottom: 8, left: 4 } },
        onHover: clickEnabled
          ? (e, els) => {
              // Guard: bail out if the chart instance has been destroyed
              if (!chartRef.current) return;
              if (e?.native?.target) {
                e.native.target.style.cursor = els.length
                  ? "pointer"
                  : "default";
              }
            }
          : undefined,
        onClick: clickEnabled
          ? (evt, _els, chart) => {
              // Guard: bail out if the chart instance has been destroyed
              if (!chartRef.current) return;
              const pts = chart.getElementsAtEventForMode(
                evt,
                "nearest",
                { intersect: true },
                true,
              );
              const first = pts[0];
              if (!first) return;
              // Bar-level drilldown: hand the index to the parent, no table.
              if (barDrilldown) {
                onBarClick?.(first.index);
                return;
              }
              // Table drilldown (original behaviour)
              setSelectedIndex(first.index);
              setPage(1);
              onPointClick?.(first.index);
            }
          : undefined,
        plugins: {
          legend: {
            position: "bottom",
            align: "center",
            labels: {
              usePointStyle: true,
              pointStyle: "rect",
              boxWidth: 20,
              boxHeight: 16,
              padding: 20,
              font: { size: 13, weight: "500" },
              color: "#4b5563",
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => {
                  // Gradient fills are functions, so fall back to borderColor
                  // (the resolved dark token) for the legend swatch.
                  const swatch =
                    ds.type === "bar"
                      ? typeof ds.backgroundColor === "string"
                        ? ds.backgroundColor
                        : typeof ds.borderColor === "string"
                          ? ds.borderColor
                          : "#38bdf8"
                      : typeof ds.borderColor === "string"
                        ? ds.borderColor
                        : "#38bdf8";
                  return {
                    text: ds.label,
                    fillStyle: swatch,
                    strokeStyle: swatch,
                    lineWidth: 0,
                    pointStyle: "rect",
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i,
                  };
                }),
            },
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            padding: 12,
            cornerRadius: 8,
            titleFont: { weight: "600", size: 13 },
            bodyFont: { size: 12 },
            boxPadding: 4,
            usePointStyle: true,
            itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
            callbacks: {
              title: (items) => {
                const di = items[0].dataIndex;
                if (typeof tooltipTitleFormatter === "function") {
                  const out = tooltipTitleFormatter(di);
                  // Chart.js accepts a string or an array of strings here.
                  if (out != null) return out;
                }
                return labels[di];
              },
              label: (item) => {
                if (typeof tooltipFormatter === "function") {
                  const out = tooltipFormatter(
                    item.dataIndex,
                    item.datasetIndex,
                  );
                  if (out == null) return undefined;
                  return Array.isArray(out) ? out : [out];
                }
                return undefined;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: stackedBars,
            grid: { display: false, drawBorder: false },
            ticks: {
              display: showXTicks,
              color: "#6b7280",
              font: { size: 11 },
              autoSkip: true,
              maxRotation: 0,
            },
            border: { display: false },
            title: {
              display: !!xAxisLabel,
              text: xAxisLabel || "",
              color: "#6b7280",
              font: { size: 13, weight: "600" },
              padding: { top: 6 },
            },
          },
          y: {
            stacked: stackedBars,
            min: yMin,
            max: yMax,
            grid: { color: "rgba(0, 0, 0, 0.06)", drawBorder: false },
            ticks: {
              color: "#9ca3af",
              font: { size: 12 },
              ...(yStepSize !== undefined ? { stepSize: yStepSize } : {}),
            },
            border: { display: false },
            title: {
              display: !!yAxisLabel,
              text: yAxisLabel || "",
              color: "#6b7280",
              font: { size: 13, weight: "600" },
              padding: { bottom: 6 },
            },
          },
          // Secondary right axis (e.g. FTR ratio drawn next to raw counts).
          ...(usesSecondaryAxis
            ? {
                y1: {
                  position: "right",
                  stacked: false,
                  min: y1Min,
                  max: y1Max,
                  grid: { drawOnChartArea: false, drawBorder: false },
                  ticks: {
                    color: "#9ca3af",
                    font: { size: 12 },
                    ...(y1StepSize !== undefined
                      ? { stepSize: y1StepSize }
                      : {}),
                  },
                  border: { display: false },
                  title: {
                    display: !!y1AxisLabel,
                    text: y1AxisLabel || "",
                    color: "#6b7280",
                    font: { size: 13, weight: "600" },
                    padding: { bottom: 6 },
                  },
                },
              }
            : {}),
        },
      },
    });

    // Cleanup: destroy synchronously — no deferral, no rAF.
    // chartRef is nulled inside destroyChart() before destroy() is called,
    // so the onHover / onClick guards catch any stale callbacks immediately.
    return () => destroyChart();
  }, [
    labels,
    datasets,
    hasData,
    loading,
    error,
    tension,
    yMin,
    yMax,
    yStepSize,
    y1Min,
    y1Max,
    y1StepSize,
    y1AxisLabel,
    stackedBars,
    showXTicks,
    xAxisLabel,
    yAxisLabel,
    clickEnabled,
    barDrilldown,
    onBarClick,
    pointDetails,
    inTable,
    onPointClick,
    tooltipFormatter,
    tooltipTitleFormatter,
  ]);

  // Reset table drilldown when labels change (e.g. parent switches subject)
  const labelsKey = labels.join("|");
  useEffect(() => {
    setSelectedIndex(null);
    setPage(1);
  }, [labelsKey]);

  // Table-drilldown derived data
  const detail = inTable ? pointDetails[selectedIndex] : null;
  const students = Array.isArray(detail?.students) ? detail.students : [];
  const totalPages = Math.max(1, Math.ceil(students.length / drilldownPerPage));
  const start = (page - 1) * drilldownPerPage;
  const pageItems = students.slice(start, start + drilldownPerPage);
  const columns =
    Array.isArray(drilldownColumns) && drilldownColumns.length > 0
      ? drilldownColumns
      : [];

  const displayTitle =
    inTable && labels[selectedIndex]
      ? `${title} — ${labels[selectedIndex]}${drilldownTitleSuffix || ""}`
      : title;

  return (
    <div className={styles.lineCard}>
      <div className={styles.lineHeader}>
        <div className={styles.lineTitle}>
          {icon && <span className={styles.lineIconWrap}>{icon}</span>}
          <h3 className="chartTitle">{displayTitle}</h3>
        </div>

        <div className={styles.lineHeaderRight}>
          {inTable ? (
            <button
              type="button"
              className={styles.lineCloseBtn}
              onClick={() => setSelectedIndex(null)}
              aria-label="Back to chart"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          ) : barDrilldown || canGoBack ? (
            // Drilldown header: note + (optional) back button. The back button
            // now renders whenever `canGoBack` is true, even on a leaf level
            // where further bar drilling is disabled (barDrilldown === false).
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                minWidth: 260,
                maxWidth: 440,
              }}
            >
              {note && (
                <div
                  className={styles.lineNoteBox}
                  style={{ flex: 1, maxWidth: "100%" }}
                >
                  <strong>{dt.note}</strong> {note}
                </div>
              )}
              {/* X = go back up one drilldown level (placed beside the note) */}
              {canGoBack && (
                <button
                  type="button"
                  className={styles.lineCloseBtn}
                  onClick={() => onBack?.()}
                  aria-label="Back"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
                minWidth: 260,
                maxWidth: 380,
              }}
            >
              {/* Mode toggle (only renders when more than one mode) */}
              {Array.isArray(modes) && modes.length > 1 && (
                <div
                  style={{
                    display: "inline-flex",
                    gap: 4,
                    alignSelf: "flex-end",
                  }}
                  role="tablist"
                >
                  {modes.map((m) => {
                    const isActive = activeMode === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onModeChange?.(m.value)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 6,
                          border: "1px solid var(--color-neutral-400, #ccc)",
                          background: isActive
                            ? "var(--blue-light, #3b82f6)"
                            : "var(--color-white, #fff)",
                          color: isActive
                            ? "#fff"
                            : "var(--color-text, #374151)",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {selectControl}
              {note && (
                <div
                  className={styles.lineNoteBox}
                  style={{ maxWidth: "100%" }}
                >
                  <strong>{dt.note}</strong> {note}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {inTable ? (
        <div className={styles.lineTableWrap}>
          {drilldownLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
              }}
            >
              <div className={styles.lineSpinner} />
            </div>
          ) : drilldownError ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
                color: "var(--text-muted, #6b7280)",
                fontSize: 14,
                textAlign: "center",
                padding: 16,
              }}
            >
              {drilldownError}
            </div>
          ) : students.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: height,
                color: "var(--text-muted, #6b7280)",
                fontSize: 14,
                textAlign: "center",
                padding: 16,
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            <>
              <div className={styles.lineTableScroll}>
                <table className={styles.lineTable}>
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className={`${styles.lineTh} ${
                            c.align === "center" ? styles.lineCenter : ""
                          }`}
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((s) => (
                      <tr key={s.id} className={styles.lineRow}>
                        {columns.map((c) => (
                          <td
                            key={c.key}
                            className={`${styles.lineTd} ${
                              c.align === "center" ? styles.lineCenter : ""
                            }`}
                          >
                            {c.render ? c.render(s) : s[c.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.linePagination}>
                <span className={styles.lineEntries}>
                  {dt.showing} {students.length === 0 ? 0 : start + 1} {dt.to}{" "}
                  {Math.min(start + drilldownPerPage, students.length)} {dt.of}{" "}
                  {students.length} {dt.entries}
                </span>
                <div className={styles.linePageBtns}>
                  <button
                    type="button"
                    className={styles.linePageBtn}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {dt.previous}
                  </button>
                  <button
                    type="button"
                    className={styles.linePageBtn}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {dt.next}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.lineChart} style={{ height }}>
          {loading && (
            <div className={styles.lineState}>
              <div className={styles.lineSpinner} />
            </div>
          )}
          {!loading && error && (
            <div className={`${styles.lineState} ${styles.lineError}`}>
              {error}
            </div>
          )}
          {!loading && !error && !hasData && (
            <div className={styles.lineState}>{emptyMessage}</div>
          )}
          {!loading && !error && hasData && <canvas ref={canvasRef} />}
        </div>
      )}
    </div>
  );
};

export default LineAndBarChart;